import { motion } from "framer-motion";
import { ArrowRight, Flame, RotateCcw, Sparkles, Target } from "lucide-react";
import { studySnapshot, questList, dailyFor } from "../../engine";
import { useGame } from "../../state/GameProvider";

/**
 * 今日 — the first screen.
 *
 * It answers four questions before the student has to decide anything:
 * where am I, what is there to do, how have I grown, and what is next.
 * Exactly one primary button, so "start studying" is never a choice between
 * options.
 */
export function TodayView() {
  const { state, actions } = useGame();
  const snap = studySnapshot(state.progress);
  const quests = questList(dailyFor(state.progress));
  const goal = snap.goal;

  const goalMode =
    goal.kind === "review" ? "review" : goal.kind === "wrong" ? "wrong" : goal.kind === "new" ? "new" : "auto";

  return (
    <div className="view today">
      <motion.section
        className="hero"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="hero-rank">
          <span className="rank-name">{snap.rank.rank.label}</span>
          <span className="rank-gloss">{snap.rank.rank.gloss}</span>
        </div>

        <div className="rank-bar" role="img" aria-label={`學階進度 ${snap.rank.percentToNext}%`}>
          <motion.i
            initial={{ width: 0 }}
            animate={{ width: `${snap.rank.percentToNext}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>
        <div className="rank-foot">
          {snap.rank.next ? (
            <>
              再破 <b>{snap.rank.toNext}</b> 門到「{snap.rank.next.label}」
            </>
          ) : (
            "已至從心之境"
          )}
        </div>

        <div className="hero-stats">
          <div>
            <b>{snap.mastered}</b>
            <span>已通章數 / {snap.total}</span>
          </div>
          <div>
            <b>{`${snap.percent}%`}</b>
            <span>全書進度</span>
          </div>
          <div>
            <b>
              <Flame size={14} /> <span>{snap.dayStreak}</span>
            </b>
            <span>連續修業</span>
          </div>
        </div>
      </motion.section>

      <motion.button
        className="goal-card"
        onClick={() => actions.start({ mode: goalMode })}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.06 }}
        whileTap={{ scale: 0.985 }}
      >
        <span className="goal-tag">
          <Target size={13} /> 下一步
        </span>
        <strong>{goal.title}</strong>
        <span className="goal-detail">{goal.detail}</span>
        <span className="goal-go">
          開始 <ArrowRight size={16} />
        </span>
      </motion.button>

      <section className="panel quests-today">
        <div className="panel-title">
          <Sparkles size={15} />
          今日修業
        </div>
        {quests.map((quest) => (
          <div key={quest.id} className={`quest${quest.done ? " done" : ""}`}>
            <div className="quest-head">
              <span>{quest.label}</span>
              <b>
                {`${quest.have}/${quest.need}`}
              </b>
            </div>
            <div className="quest-bar">
              <motion.i
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(100, (quest.have / quest.need) * 100)}%` }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              />
            </div>
          </div>
        ))}
      </section>

      <div className="quick-row">
        <button onClick={() => actions.start({ mode: "review" })} disabled={snap.due === 0}>
          <RotateCcw size={15} />
          複習 {snap.due > 0 && <b>{snap.due}</b>}
        </button>
        <button
          onClick={() => actions.start({ mode: "wrong" })}
          disabled={(state.progress.misses?.length ?? 0) === 0}
        >
          <Target size={15} />
          攻錯 {(state.progress.misses?.length ?? 0) > 0 && <b>{state.progress.misses.length}</b>}
        </button>
        <button onClick={() => actions.start({ mode: "new" })}>
          <Sparkles size={15} />
          新章
        </button>
      </div>

      {snap.weakest && snap.weakest.percent < 100 && (
        <button className="weak-hint" onClick={() => actions.setView("map")}>
          最弱的一條是「{snap.weakest.concept.label}」（{snap.weakest.percent}%）——{snap.weakest.concept.question}
        </button>
      )}
    </div>
  );
}
